import React, { useState } from 'react';
import { BookOpen, GraduationCap, AlignCenterVertical as Certificate, Send, Phone, Mail, MapPin } from 'lucide-react';

interface ContactProps {
  isDarkMode?: boolean;
}

const Contact: React.FC<ContactProps> = ({ isDarkMode = false }) => {
  const [activeTab, setActiveTab] = useState('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });

  const resources = [
    {
      icon: BookOpen,
      title: "Security Best Practices Guide",
      description: "Comprehensive guide to implementing cybersecurity best practices in your organization."
    },
    {
      icon: GraduationCap,
      title: "Cybersecurity Training Programs",
      description: "Professional development courses for your IT team and security awareness for all employees."
    },
    {
      icon: Certificate,
      title: "Compliance Checklist",
      description: "Industry-specific compliance requirements and implementation roadmaps."
    }
  ];

  const predefinedQuestions = [
    "What cybersecurity services do you offer?",
    "How can you help with compliance requirements?",
    "What is your incident response process?",
    "Do you provide 24/7 monitoring services?",
    "Can you help with security training for employees?",
    "What are your pricing models?",
    "How do you handle data privacy and protection?",
    "Do you offer penetration testing services?"
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleQuestionClick = (question: string) => {
    setFormData({
      ...formData,
      message: question
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission here
  };

  return (
    <section id="contact" className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
            isDarkMode ? 'text-slate-100' : 'text-slate-900'
          }`}>
            Ready to Secure Your Business?
          </h2>
          <p className={`text-xl max-w-3xl mx-auto ${
            isDarkMode ? 'text-slate-300' : 'text-slate-600'
          }`}>
            Get started with a comprehensive security assessment and discover how 
            CyberSoft can protect your organization from cyber threats.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form with Tabs */}
          <div>
            <div className={`rounded-xl shadow-lg p-8 ${
              isDarkMode ? 'bg-slate-800' : 'bg-white'
            }`}>
              {/* Tab Navigation */}
              <div className={`flex border-b mb-6 ${
                isDarkMode ? 'border-slate-700' : 'border-slate-200'
              }`}>
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`px-6 py-3 font-semibold transition-colors ${
                    activeTab === 'contact'
                      ? (isDarkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600')
                      : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                  }`}
                >
                  Contact Form
                </button>
                <button
                  onClick={() => setActiveTab('questions')}
                  className={`px-6 py-3 font-semibold transition-colors ${
                    activeTab === 'questions'
                      ? (isDarkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600')
                      : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                  }`}
                >
                  Quick Questions
                </button>
              </div>

              {/* Contact Form Tab */}
              {activeTab === 'contact' && (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                          isDarkMode 
                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-400' 
                            : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-500'
                        }`}
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                          isDarkMode 
                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-400' 
                            : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-500'
                        }`}
                        placeholder="your.email@company.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="company" className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                          isDarkMode 
                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-400' 
                            : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-500'
                        }`}
                        placeholder="Your company"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                          isDarkMode 
                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-400' 
                            : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-500'
                        }`}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-700'
                    }`}>
                      Service Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                        isDarkMode 
                          ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-400' 
                          : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-500'
                      }`}
                    >
                      <option value="">Select a service</option>
                      <option value="managed-security">Managed Security Services</option>
                      <option value="consulting">Security Consulting</option>
                      <option value="penetration-testing">Penetration Testing</option>
                      <option value="compliance">Compliance Management</option>
                      <option value="training">Security Training</option>
                      <option value="incident-response">Incident Response</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-700'
                    }`}>
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                        isDarkMode 
                          ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-400' 
                          : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-500'
                      }`}
                      placeholder="Tell us about your cybersecurity needs..."
                    />
                  </div>

                  <button
                    type="submit"
                    className={`w-full px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center ${
                      isDarkMode 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    Send Message
                    <Send className="ml-2 h-5 w-5" />
                  </button>
                </form>
              )}

              {/* Quick Questions Tab */}
              {activeTab === 'questions' && (
                <div className="space-y-4">
                  <p className={`mb-6 ${
                    isDarkMode ? 'text-slate-300' : 'text-slate-600'
                  }`}>
                    Click on any question below to quickly add it to your message:
                  </p>
                  <div className="space-y-3">
                    {predefinedQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuestionClick(question)}
                        className={`w-full text-left p-4 border rounded-lg transition-colors ${
                          isDarkMode 
                            ? 'bg-slate-700 hover:bg-slate-600 border-slate-600 hover:border-blue-400' 
                            : 'bg-slate-50 hover:bg-blue-50 border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        <span className={isDarkMode ? 'text-slate-200 hover:text-blue-300' : 'text-slate-700 hover:text-blue-700'}>{question}</span>
                      </button>
                    ))}
                  </div>
                  <div className={`mt-6 p-4 rounded-lg ${
                    isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                  }`}>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-blue-300' : 'text-blue-700'
                    }`}>
                      <strong>Selected Question:</strong>
                    </p>
                    <p className={`mt-1 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      {formData.message || "No question selected yet"}
                    </p>
                    <button
                      onClick={() => setActiveTab('contact')}
                      className={`mt-3 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        isDarkMode 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      Go to Contact Form
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Contact Information and Resources */}
          <div className="space-y-8">
            {/* Contact Information */}
            <div className={`rounded-xl shadow-lg p-8 ${
              isDarkMode ? 'bg-slate-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 flex items-center ${
                isDarkMode ? 'text-slate-100' : 'text-slate-900'
              }`}>
                <Phone className={`h-6 w-6 mr-2 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
                Get In Touch
              </h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-3">
                  <MapPin className={`h-5 w-5 mt-1 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <div>
                    <h4 className={`font-semibold mb-1 ${
                      isDarkMode ? 'text-slate-100' : 'text-slate-900'
                    }`}>India Office</h4>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-600'
                    }`}>
                      Cyber Tower, Hitech City<br />
                      Hyderabad, Telangana 500081
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Phone className={`h-5 w-5 mt-1 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <div>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-600'
                    }`}>+91 40 4567 8900</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className={`h-5 w-5 mt-1 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <div>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-600'
                    }`}>india@cybersoft.com</p>
                  </div>
                </div>
                
                <div className={`border-t pt-4 ${
                  isDarkMode ? 'border-slate-700' : 'border-slate-200'
                }`}>
                  <div className="flex items-start space-x-3">
                    <MapPin className={`h-5 w-5 mt-1 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <div>
                      <h4 className={`font-semibold mb-1 ${
                        isDarkMode ? 'text-slate-100' : 'text-slate-900'
                      }`}>Australia Office</h4>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>
                        Level 40, 140 William Street<br />
                        Melbourne, VIC 3000
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 mt-2">
                    <Phone className={`h-5 w-5 mt-1 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <div>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>+61 39876 5432</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 mt-2">
                    <Mail className={`h-5 w-5 mt-1 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <div>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>australia@cybersoft.com</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Free Resources */}
            <div className={`rounded-xl shadow-lg p-8 ${
              isDarkMode ? 'bg-slate-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 ${
                isDarkMode ? 'text-slate-100' : 'text-slate-900'
              }`}>Free Resources</h3>
              <div className="space-y-4">
                {resources.map((resource, index) => (
                  <div key={index} className={`p-4 rounded-lg transition-colors cursor-pointer ${
                    isDarkMode 
                      ? 'bg-slate-700 hover:bg-slate-600' 
                      : 'bg-slate-50 hover:bg-slate-100'
                  }`}>
                    <resource.icon className={`h-6 w-6 mb-2 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <h4 className={`font-semibold mb-1 text-sm ${
                      isDarkMode ? 'text-slate-100' : 'text-slate-900'
                    }`}>{resource.title}</h4>
                    <p className={`text-xs ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-600'
                    }`}>{resource.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;